<template>
  <fieldset class="price-planning">
    <div class="plan-selector-container">
      <div
        v-for="{ plan } of visiblePlans"
        :key="plan.planKind"
        :class="['plan-selector', kebabCase(getShortKind(plan.planKind)), { active: selectedPlan === plan.planKind }]"
        @click="() => setSelectedPlan(getShortKind(plan.planKind))"
      >
        <span>{{ startCase(getShortKind(plan.planKind)) }}</span>
      </div>
    </div>
    <div
      v-if="isGourmetRegistration"
      class="message-box"
    >
      <h2 class="message-box__title">
        Two Weeks Free!
      </h2>
      <p class="message-box__content">
        GoGo is a membership-based service, but we're giving you two weeks of free membership to try us out. Choose a membership below - you
        won't owe a membership fee until the end of your two week trial.
      </p>
    </div>
    <div class="row">
      <el-switch
        v-model="billingPeriod"
        active-color="#13ce66"
        inactive-color="#c1c1c1"
        :inactive-text="$t('membershipPlan.billedMonthly')"
        inactive-value="monthly"
        :active-text="$t('membershipPlan.billedAnnually')"
        active-value="annual"
        class="bill-type-switch"
      />
    </div>

    <div
      class="comparison-container"
      :class="{ hide: !isShowComparison }"
    >
      <section
        class="main-plan-features-section mt-20"
      >
        <div
          class="highlight-column"
          :style="highlightCssVars"
        />

        <PlanHeader
          :trialRenderConfig="trialRenderConfig"
        />

        <div class="membership-benefit-item yellow-background">
          <div class="feature-title">
            {{ $t('membershipPlan.gogoGuardian') }}
          </div>
          <div class="feature-comment">
            <span> {{ $t('membershipPlan.vettedDrivers') }} </span>&nbsp; <span>-</span>&nbsp;
            <a
              href="https://gogograndparent.com/#features-and-services-section"
              target="_blank"
            >{{
              $t('membershipPlan.clickToLearnMore')
            }}</a>
          </div>
        </div>
        <PlanBenefitItem
          :isOperatorRegistration="isOperatorRegistration"
          :benefit-item="priceBenefits"
        />
        <template
          v-for="(detailItem, detailIndex) in priceBenefits.details"
          :key="detailIndex + 30"
        >
          <PlanBenefitItem
            :isOperatorRegistration="isOperatorRegistration"
            :benefit-item="detailItem"
          />
          <PlanBenefitItem
            v-for="(subDetailItem, subDetailIndex) in detailItem.details"
            :key="subDetailIndex + 30"
            class="sub-child-feature"
            :benefitItem="subDetailItem"
            :isOperatorRegistration="isOperatorRegistration"
          />
        </template>
        <template
          v-for="(item, index) in mainBenefitList"
          :key="index"
        >
          <PlanBenefitItem
            :isOperatorRegistration="isOperatorRegistration"
            :benefitItem="item"
          />

          <template
            v-for="(detailItem, detailIndex) in item.details"
            :key="detailIndex + 30"
          >
            <PlanBenefitItem
              :isOperatorRegistration="isOperatorRegistration"
              :benefitItem="detailItem"
            />

            <PlanBenefitItem
              v-for="(subDetailItem, subDetailIndex) in detailItem.details"
              :key="subDetailIndex + 30"
              class="sub-child-feature"
              :benefitItem="subDetailItem"
              :isOperatorRegistration="isOperatorRegistration"
            />
          </template>
        </template>

        <PlanFooter
          :isCurrentPlanBtnDisable="isCurrentPlanBtnDisable"
          :isOperatorRegistration="isOperatorRegistration"
          :hideSelectPackage="hideSelectPackage"
          @chooseMembership="chooseMembership"
        />
      </section>

      <div
        class="detail-section-action-container"
        @click="showHideDetailFeature()"
      >
        <div class="detail-title">
          {{ $t('membershipPlan.detailedPlanComparison') }}
        </div>
        <div class="container-status">
          {{ isDetailShow ? $t('membershipPlan.hide') : $t('membershipPlan.show') }}
        </div>
        <i
          class="el-icon-arrow-up"
          :class="{ rotate: !isDetailShow }"
        />
      </div>

      <section
        v-if="isDetailShow"
        class="detail-plan-features-section"
      >
        <div class="membership-content">
          <div
            class="highlight-column"
            :style="highlightCssVars"
          />
          <PlanHeader
            :isPriceShow="false"
          />

          <div class="membership-benefit-item gray-background">
            <div class="feature-title">
              {{ $t('membershipPlan.familyFeatures') }}
            </div>
            <div class="feature-comment">
              {{ $t('membershipPlan.forLovedOnes') }}
            </div>
          </div>

          <PlanBenefitItem
            v-for="(item, index) in familyBenefitList"
            :key="index + 100"
            :benefitItem="item"
            :isOperatorRegistration="isOperatorRegistration"
          />

          <div class="membership-benefit-item gray-background">
            <div class="feature-title">
              {{ $t('membershipPlan.extraFeatures') }}
            </div>
            <div class="feature-comment">
              {{ $t('membershipPlan.seeEverythingToOffer') }}
            </div>
          </div>

          <PlanBenefitItem
            v-for="(item, index) in extraBenefitList"
            :key="index + 1000"
            :benefitItem="item"
            :isOperatorRegistration="isOperatorRegistration"
          />

          <PlanFooter
            :isCurrentPlanBtnDisable="isCurrentPlanBtnDisable"
            :isOperatorRegistration="isOperatorRegistration"
            :hideSelectPackage="hideSelectPackage"
            @choose-membership="chooseMembership"
          />
        </div>
      </section>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { ref, toRefs, computed, watch, onMounted, defineAsyncComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';

import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { usePricePlan } from '@/use/pricePlan';
import { billingPeriod, getShortKind, isAnnualPlanning, visiblePlans, type BenefitItemView } from '@/services/pricing';

import { startCase, kebabCase } from 'lodash';

const PlanHeader = defineAsyncComponent(() => import('./PricePlanning/PlanHeader.vue') as any);

const PlanBenefitItem = defineAsyncComponent(() => import('./PricePlanning/PlanBenefitItem.vue') as any);

const PlanFooter = defineAsyncComponent(() => import('./PricePlanning/PlanFooter.vue') as any);

const props = defineProps({
  isCurrentPlanBtnDisable: {
    type: Boolean,
    default: false,
  },
  isOperatorRegistration: {
    type: Boolean,
    default: false,
  },
  showDetailedPlanComparison: {
    type: Boolean,
    default: true,
  },
  hideSelectPackage: {
    type: Boolean,
    default: false,
  },
  isGourmetRegistration: {
    type: Boolean,
    default: false,
  },
  trialRenderConfig: {
    type: Object as PropType<any>,
    default: null,
  },
});

const emit = defineEmits(['choose-membership', 'choose-annual-type']);

const { t } = useI18n();

const { customerPlans } = useCustomer();
const {
  isBasicMember,
  isBasicAnnualMember,
  isValueMember,
  isValueAnnualMember,
  isFixedIncomeMember,
  isFixedIncomeAnnualMember,
  isPremiumMember,
  isPremiumAnnualMember,
  isTotalCareMember,
  isTotalCareAnnualMember,
} = toRefs(customerPlans);
const { selectedPlan, setSelectedPlan } = usePricePlan();

const { isFamilyDashboard } = useDashboard();

const isDetailShow = ref(props.showDetailedPlanComparison);
const isShowComparison = ref(true);

const highlightCssVars = computed(() => {
  const highlightIndex = visiblePlans.value.findIndex((p) => p.pricing.accent);
  return {
    '--column-count': visiblePlans.value.length + 1,
    '--highlight-index': highlightIndex + 1,
    '--has-highlight': highlightIndex >= 0 ? 1 : 0,
  };
});

const priceBenefits = computed<BenefitItemView>(() => {
  return {
    title: t('membershipPlan.pricing'),
    tooltipText: t('membershipPlan.pricingTooltipText'),
    isParent: true,
    details: [
      {
        title: t('membershipPlan.rides'),
        field: 'ridePaymentTier',
        details: [
          {
            title: t('membershipPlan.operatorOrdered'),
            tooltipText: t('membershipPlan.operatorOrderedTooltipText'),
            field: 'operatorOrderedRidePrice',
            default: 'Unlimited',
          },
        ],
      },
      {
        title: t('membershipPlan.mealDelivery'),
        tooltipText: t('membershipPlan.mealDeliveryTooltipText'),
        field: 'gourmetPaymentTier',
      },
      {
        title: t('membershipPlan.groceryDelivery'),
        tooltipText: t('membershipPlan.groceryDeliveryTooltipText'),
        field: 'groceryPaymentTier',
      },
      {
        title: t('membershipPlan.pharmacy'),
        field: 'pharmacyPaymentTier',
      },
      {
        title: t('membershipPlan.specialRequest'),
        field: 'specialRequestPaymentTier',
      },
    ],
  };
});

const mainBenefitList = computed<BenefitItemView[]>(() => [
  {
    title: t('membershipPlan.homeServices'),
    tooltipText: t('membershipPlan.homeServicesTooltipText'),
    isParent: true,
    details: [
      {
        title: t('membershipPlan.oneTimeRequest'),
        field: 'homeServiceOneTimePaymentTier',
      },
      {
        title: t('membershipPlan.recurringRequest'),
        field: 'homeServiceRecurringPaymentTier',
      },
    ],
  },
  {
    title: t('membershipPlan.prioritySupport'),
    tooltipText: t('membershipPlan.prioritySupportTooltipText'),
    field: 'prioritySupport',
    isParent: true,
  },
  {
    title: t('membershipPlan.proactiveCareManagement'),
    tooltipText: t('membershipPlan.proactiveCareManagementTooltipText'),
    field: 'proactiveCareManagement',
    isParent: true,
  },
  {
    title: t('membershipPlan.rideCredits'),
    tooltipText: t('membershipPlan.rideCreditsTooltipText'),
    field: 'freeRides',
    isParent: true,
  },
  {
    title: t('membershipPlan.groceryCredits'),
    tooltipText: t('membershipPlan.groceryCreditsTooltipText'),
    field: 'freeGroceries',
    isParent: true,
  },
]);

const familyBenefitList = computed<BenefitItemView[]>(() => [
  {
    title: t('membershipPlan.addMembers'),
    tooltipText: t('membershipPlan.addMembersTooltipText'),
    field: 'lovedOnesLimit',
    isParent: true,
  },
  {
    title: t('membershipPlan.dashboard'),
    tooltipText: t('membershipPlan.dashboardTooltipText'),
    field: 'dashboard',
    isParent: true,
  },
  {
    title: t('membershipPlan.activeHours'),
    tooltipText: t('membershipPlan.activeHoursTooltipText'),
    field: 'activeHours',
    isParent: true,
    isNewFeature: true,
  },
  {
    title: t('membershipPlan.emergencyCalls'),
    tooltipText: t('membershipPlan.emergencyCallsTooltipText'),
    field: 'emergencyCalls',
    isParent: true,
    isNewFeature: true,
  },
  {
    title: t('membershipPlan.splitPayments'),
    tooltipText: t('membershipPlan.splitPaymentsTooltipText'),
    field: 'splitPayments',
    isParent: true,
    isNewFeature: true,
  },
  {
    title: t('membershipPlan.turninfOffReceipts'),
    tooltipText: t('membershipPlan.turninfOffReceiptsTooltipText'),
    field: 'turningOffReceipts',
    isParent: true,
    isNewFeature: true,
  },
]);

const extraBenefitList = computed<BenefitItemView[]>(() => [
  {
    title: t('membershipPlan.groceryStandingOrder'),
    tooltipText: t('membershipPlan.groceryStandingOrderTooltipText'),
    field: 'groceryStandingOrder',
    isParent: true,
  },
  {
    title: t('membershipPlan.authorizedGuestTrips'),
    tooltipText: t('membershipPlan.authorizedGuestTripsTooltipText'),
    field: 'authorizedGuestTrips',
    isParent: true,
  },
  {
    title: t('membershipPlan.requestSameOperator'),
    tooltipText: t('membershipPlan.requestSameOperatorTooltipText'),
    field: 'favoriteOperators',
    isParent: true,
    isNewFeature: true,
  },
  {
    title: t('membershipPlan.insuranceTaxStatements'),
    tooltipText: t('membershipPlan.insuranceTaxStatementsTooltipText'),
    field: 'insuranceAndTaxStatements',
    isParent: true,
  },
  {
    title: t('membershipPlan.wellnessChecks'),
    tooltipText: t('membershipPlan.wellnessChecksTooltipText'),
    field: 'wellnessChecks',
    isParent: true,
    isNewFeature: true,
  },
]);

const chooseMembership = (plan: string, planName: string) => {
  emit('choose-membership', plan, planName);
};

const showHideDetailFeature = () => {
  isDetailShow.value = !isDetailShow.value;
};

watch(isAnnualPlanning, (value) => emit('choose-annual-type', value), {
  immediate: true,
});

onMounted(() => {
  if (!isFamilyDashboard.value) {
    return;
  }

  if (isBasicMember.value || isValueMember.value || isFixedIncomeMember.value || isPremiumMember.value || isTotalCareMember.value) {
    billingPeriod.value = 'monthly';
  }

  if (isBasicAnnualMember.value || isValueAnnualMember.value || isFixedIncomeAnnualMember.value || isPremiumAnnualMember.value || isTotalCareAnnualMember.value) {
    billingPeriod.value = 'annual';
  }
});
</script>

<style lang="scss">
.price-planning {
  font-family: sans-serif;
  .comparison-container.hide {
    display: none;
  }

  .main-plan-features-section,
  .detail-plan-features-section {
    border: 1px solid #c1c1c1;
    position: relative;
    padding: 0;
  }

  .highlight-column {
    position: absolute;
    top: 0;
    height: calc(100% + 12px);
    left: calc(100% / var(--column-count) * var(--highlight-index));
    width: calc(100% / var(--column-count));
    background: #ebf2f2;
    z-index: 0;
    box-shadow: 0 5px 10px 3px #666599;
    pointer-events: none;
    opacity: var(--has-highlight);

    @media (max-width: 576px) {
      display: none;
    }
  }

  .feature-title,
  .basic-plan,
  .value-plan,
  .fixed-income-plan,
  .premium-plan,
  .total-care-plan {
    padding: 5px 10px;
    border-right: 1px solid #c1c1c1;
    align-items: center;
    &:last-child {
      border-right: 0;
    }

    @media (max-width: 576px) {
      width: 50%;
      display: none;
    }
  }

  .feature-title,
  .basic-plan.active,
  .basic-annual-plan.active,
  .value-plan.active,
  .fixed-income-plan.active,
  .premium-plan.active,
  .total-care-plan.active {
    @media (max-width: 576px) {
      display: block;
    }
  }
  .gray-background,
  .yellow-background {
    align-items: center;
    padding: 10px 0;
    .feature-title {
      border-right: 0;
      cursor: unset;
      flex: none;
    }
    span {
      color: #231f20;
    }
    a {
      color: rgb(86, 85, 160);
      text-decoration: underline;
      font-weight: bold;
    }
  }
  .yellow-background {
    background: #f4e5a5;
  }
  .gray-background {
    background: #cfd3d3;
  }
  .feature-comment {
    font-size: 16px;
    padding-left: 20px;
    padding-right: 5px;
  }
  .detail-section-action-container {
    display: flex;
    align-items: center;
    padding: 40px 10px 30px;
    width: fit-content;
  }
  .detail-title,
  .container-status,
  i {
    font-size: 18px;
    font-weight: bold;
    padding-right: 20px;
    cursor: pointer;
  }
  .container-status {
    width: 70px;
    padding-right: 10px;
  }
  i {
    transition: all 0.3s;
    transform: rotate(0deg);
    padding-right: 0;
    &.rotate {
      transform: rotate(180deg);
      transition: all 0.3s;
    }
  }
  .detail-plan-features-section {
    margin-bottom: 25px;
  }
  .bill-type-switch {
    width: 400px;
    margin: 20px 20px 30px;

    .el-switch__action {
      width: 38px;
      height: 38px;
      top: 0;
    }

    &.is-checked {
      .el-switch__core .el-switch__action {
        top: 0;
        margin-left: -20px;
      }
    }

    span.el-switch__label {
      font-weight: bold;
      width: 110px;

      &.el-switch__label--left {
        text-align: right;
      }

      &.el-switch__label--right {
        text-align: center;
      }
    }

    .el-switch__core {
      width: 120px !important;
      height: 40px;
      border-radius: 20px;
    }
  }

  .plan-selector-container {
    display: none;
    @media (max-width: 576px) {
      display: flex;
      margin: 20px 5px 10px;
    }

    .plan-selector {
      flex: 1;
      background: #c5c5d1;
      margin: 0 5px;
      text-align: center;
      padding: 5px 0;
      border-radius: 5px;

      &.active {
        box-shadow: 0px 2px 15px 5px #5d5b54;
        &.basic {
          background: #666699;
          color: #fff;
        }

        &.value {
          background: #f7cc1a;
        }

        &.premium {
          background: #2e2c68;
          color: #fff;
        }

        &.total-care {
          background: #754e96;
          color: #fff;
        }
      }
    }
  }
}

.price-tooltip {
  max-width: 200px;
  font-size: 14px !important;
  background: #312b76 !important;
  color: #fff;
  line-height: 20px !important;
  padding: 15px 20px !important;
  &.el-tooltip__popper .popper__arrow {
    border-top-color: #312b76 !important;
    &::after {
      border-top-color: #312b76 !important;
    }
  }

  .message-box {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    justify-content: center;
    background-color: #ece9fc;
    border-radius: 20px;
    padding: 20px 30px;
    color: #000;
    line-height: 1.6;
    max-width: 600px;
    margin: 2rem auto;
    text-align: center;
    &__title {
      font-size: 1.5rem;
      font-weight: bold;
      margin-bottom: 10px;
    }
    &__content {
      font-size: 1rem;
      margin: 0;
    }
  }
}
</style>
